import { useMemo } from 'react'

import buildUrl from 'modules/core/lib/buildUrl'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { SPACES } from 'modules/documents/lib/constants'
import type { SPACE } from 'modules/documents/lib/constants'

import type { TrashBinDocumentNode } from './types'

export function useColumns(space: SPACE) {
  const isEmployeeSpace = space === SPACES.EMPLOYEE_MY_DOCUMENTS

  const columns = useMemo(
    () => [
      {
        label: '',
        width: 45,
        render: (item: TrashBinDocumentNode) => {
          if (!item.contentType) return { type: 'text' as const, value: undefined }

          return {
            type: 'file' as const,
            value: {
              name: item.filename,
              type: item.extension ?? item.contentType.split('/').pop() ?? '',
            },
          }
        },
      },
      {
        label: i18n.t('documents.employee.list.filename'),
        sorting: 'filename' as const,
        width: 400,
        render: (item: TrashBinDocumentNode) => ({
          type: 'text' as const,
          value: item.filename,
        }),
      },
      ...(isEmployeeSpace
        ? [
            {
              label: i18n.t('documents.employee.list.employee'),
              render: (item: TrashBinDocumentNode) => {
                const employee = item.employee
                if (!employee?.fullName) {
                  return { type: 'text' as const, value: '-' }
                }

                return {
                  type: 'person' as const,
                  value: {
                    firstName: employee.fullName,
                    lastName: '',
                    src: employee.access?.avatar ? buildUrl(employee.access.avatar) : undefined,
                  },
                }
              },
            },
          ]
        : []),
      {
        label: i18n.t('documents.employee.list.deleted_at'),
        sorting: 'deletedAt' as const,
        width: 150,
        render: (item: TrashBinDocumentNode) => ({
          type: 'date' as const,
          value: dateBackendDateTimeToUI(item.deletedAt),
        }),
      },
      {
        label: i18n.t('documents.employee.list.folder'),
        render: (item: TrashBinDocumentNode) => {
          const folder = item.folder
          if (!folder?.name) {
            return { type: 'text' as const, value: '-' }
          }

          return {
            type: 'text' as const,
            value: folder.name,
          }
        },
      },
    ],
    [isEmployeeSpace]
  )

  return {
    sortings: {
      filename: {
        label: i18n.t('documents.employee.list.filename'),
      },
      deletedAt: {
        label: i18n.t('documents.employee.list.deleted_at'),
      },
    },
    defaultSortings: {
      field: 'deletedAt' as const,
      order: 'desc' as const,
    },
    columns,
  }
}
